import { countryByIdFilter } from "app/filters/ngFilters";

import { CATEGORIES_COMPARISON_COLORS } from "../../categories-context/CategoriesContext";
import type {
    CountryDataItem,
    GeographyTableData,
    GeographyComparisonTableRow,
    ComparisonMapDataItem,
    SingleMapDataItem,
} from "../types";

import { filterTableData, sortTableData } from "./utils";

export function processTableContainerData(
    tableData: GeographyTableData,
    searchString: string,
    sortedColumn: { field: string; sortDirection: "desc" | "asc" },
    tableSelectionProperty: string,
    countryTextByIdFilter: () => (val: any, na?: any) => string,
) {
    if (!tableData?.Data) {
        return [];
    }

    const filteredData = filterTableData(tableData.Data, searchString, tableSelectionProperty, countryTextByIdFilter);

    return sortTableData(filteredData, sortedColumn.field, sortedColumn.sortDirection);
}

export function processMapData(data: CountryDataItem[] | undefined): SingleMapDataItem[] {
    if (!data) {
        return [];
    }

    const getCountryById = countryByIdFilter();

    return data.map((item) => ({
        "hc-key": getCountryById(item.Country).code,
        value: item.TrafficShare,
    }));
}

export function processMapDataComparison(datasets: GeographyTableData[] | undefined): ComparisonMapDataItem[] {
    if (!datasets || datasets.length < 2) {
        return [];
    }

    const getCountryById = countryByIdFilter();
    const [primaryDataset, comparisonDataset] = datasets;
    const primaryData = primaryDataset?.Data;
    const comparisonData = comparisonDataset?.Data;

    return (
        primaryData?.map((country) => {
            const countryKey = getCountryById(country.Country).code;
            const comparedValue = comparisonData?.find((item) => item.Country === country.Country)?.TrafficShare ?? 0;

            return {
                "hc-key": countryKey,
                primaryValue: country.TrafficShare,
                comparedValue: comparedValue,
                color:
                    country.TrafficShare > comparedValue
                        ? CATEGORIES_COMPARISON_COLORS[0]
                        : CATEGORIES_COMPARISON_COLORS[1],
            };
        }) || []
    );
}

export function buildComparisonTableData(data: GeographyTableData[]): GeographyComparisonTableRow[] {
    if (data.length < 2) {
        return [];
    }

    const [primaryData, comparedData] = data;

    // Create maps for efficient country lookup
    const primaryCountries = new Map(primaryData?.Data?.map((item) => [item.Country, item]) || []);
    const comparedCountries = new Map(comparedData?.Data?.map((item) => [item.Country, item]) || []);

    // Get all unique countries from both datasets
    const allCountries = new Set([...primaryCountries.keys(), ...comparedCountries.keys()]);

    const rows: GeographyComparisonTableRow[] = [];

    // Process each country and match data by Country ID
    for (const countryId of allCountries) {
        const primaryItem = primaryCountries.get(countryId);
        const comparedItem = comparedCountries.get(countryId);

        rows.push({
            country: countryId,
            primaryCategory: primaryItem
                ? {
                      trafficShare: primaryItem.TrafficShare,
                      visits: primaryItem.Visits,
                      growth: primaryItem.Growth,
                      change: primaryItem.GrowthPercentage,
                  }
                : null,
            comparedCategory: comparedItem
                ? {
                      trafficShare: comparedItem.TrafficShare,
                      visits: comparedItem.Visits,
                      growth: comparedItem.Growth,
                      change: comparedItem.GrowthPercentage,
                  }
                : null,
        });
    }

    return rows;
}
